/**
 * NewImportApiModal Component
 * ----------------------------------------------------------
 * PURPOSE:
 * This modal manages importing **API Data** (.csv / .xlsx / .xls)
 * into the system. It provides:
 *
 *  ✔ Downloadable CSV template containing valid API headers
 *  ✔ Upload functionality with server-side validation & processing
 *  ✔ Table view of historical uploads (API import job logs)
 *
 * FEATURES:
 *  - Displays:
 *      • Request ID
 *      • Created At timestamp
 *      • Status of API import
 *      • Upload Count
 *      • Uploaded original file link
 *      • Processed file link (if available)
 *
 * PROPS:
 *  open            Boolean – controls modal visibility
 *  close           Function – closes the modal
 *  organization_id Organization identifier (string)
 *  user            Logged-in user info
 *  apiHeaders      API header configuration coming from Redux
 *  theme           Light/Dark theme preference
 *
 * API Services Used:
 *  fetchApiFile()  → Fetch API import logs
 *  uploadApiFile() → Upload API data file
 */

import { FunctionComponent, useEffect, useState } from "react";
import styles from "./ImportModal.module.css";
import {fetchApiFile,uploadApiFile,
} from "../../../Services/contacts";
import Loading from "../../Loading/Loading";
import moment from "moment";
import { connect, useDispatch } from "react-redux";
import { themeColors } from "../../theme";
import Modal, { Styles } from "react-modal";
import CSVGenerator from "./CSVGenerator";
import StyledSubmitButton from "../../Buttons/StyledSubmitButton";
import "../../../theme-vars.css";
import { useMediaQuery, useTheme } from "@mui/material";
import StyledCancelButton from "../../Buttons/StyledCancelButton";

type props = {
    open: boolean;
    close: () => void;
    organization_id: string;
    history: any;
    usersList: any[];
    user: any;
    theme: any;
    apiHeaders: any;
};

const NewImportApiModal: FunctionComponent<props> = ({
    open,
    close,
    organization_id,
    history,
    usersList,
    user,
    theme,
    apiHeaders,
}) => {

    /** MUI theme hooks for mobile responsiveness */
    const themeMUI = useTheme();
    const isMobile = useMediaQuery(themeMUI.breakpoints.down("sm"));

    const [load, setLoad] = useState(false);               // Loader state during upload
    const [uploadedFiles, setUploadedFiles] = useState<any[]>([]);  // Upload history list
    const dispatcher = useDispatch();

    /** Interface defining CSV row structure for CSV template generator */
    interface DataObject {
        [key: string]: any;
    }

    /**
     * FILTER HEADERS FOR API TEMPLATE
     * -----------------------------------------------------------------
     * filtervalues represents final allowed fields for API upload.
     * apiHeaders (from Redux) contains system header definitions.
     */
    const filtervalues = [
        "budget", "created_by", "property_stage", "location",
        "customer_name", "contact_no", "alternate_no", "country_code",
        "associate_contact", "email", "contact_owner_email", "status",
        "property_type", "stage", "fail_reason", "project", "lead_source"
    ];

    /** Filter the full API header list to only include supported columns */
    const reqData = apiHeaders.filter((obj: any) => filtervalues.includes(obj.label));

    /** Add Status as extra editable field for template download */
    const dbData = [
        ...reqData,
        { label: "status", value: "Status" }
    ];

    /** Convert objects to CSV header row */
    const headerData = dbData.map(item => item.value);
    const apiHeaderData: DataObject[] = [];
    apiHeaderData.push(headerData);

    /**
     * FETCH PREVIOUS API IMPORT FILE LOGS
     * -----------------------------------------------------------------
     * Whenever organization_id changes, pull fresh data import logs.
     */
    useEffect(() => {
        if (organization_id) {
            const unsub = fetchApiFile(
                organization_id,
                (data: any) => setUploadedFiles(data),
                dispatcher
            );
            return () => unsub();
        }
    }, [organization_id]);

    /**
     * HANDLE NEW API FILE UPLOAD
     * -----------------------------------------------------------------
     * Accepts file input → sends to backend → backend performs processing.
     */
    const upload = async (file: any) => {
        setLoad(true);
        uploadApiFile(
            file,
            organization_id,
            dispatcher,
            (data: boolean) => setLoad(data),
            `${user.user_first_name} ${user.user_last_name}` // uploaded by
        );
    };

    /** Themed table styles */
    const tdStyles = {
        backgroundColor: theme ? themeColors.tdBackgroundColor : themeColors.tdBackgroundColorDark,
        color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
    };

    const thStyles = {
        backgroundColor: theme ? themeColors.thBackgroundColor : themeColors.thBackgroundColorDark,
    };

    /**
     * Custom React Modal Styling
     */
    const modalStyle: Styles = {
        overlay: {
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            zIndex: 1300,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
        },
        content: {
            background: theme ? themeColors.cardBackgroundColor : themeColors.cardBackgroundColorDark,
            color: theme ? themeColors.textPrimary : themeColors.textPrimaryDark,
            borderRadius: 16,
            padding: isMobile ? "24px 12px" : "10px 20px",
            border: "none",
            boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            position: "relative",
            margin: "auto",
            inset: "auto",
        },
    };

    return (
        <>
            <Modal
                isOpen={open}
                onRequestClose={close}
                shouldCloseOnOverlayClick={true}
                shouldCloseOnEsc={true}
                style={modalStyle}
                ariaHideApp={false}
            >
                {load && <Loading />}

                <div className={styles.child}>
                    <h5
                        style={{
                            fontFamily: "var(--font-family-main)",
                            fontSize: "var(--font-size-large)",
                            paddingTop: isMobile ? "unset" : "15px"
                        }}
                    >
                        Import Data for "Api Data Form"
                    </h5>

                    {/* TABLE: Previous Upload History */}
                    <div className={styles.tableBox}>
                        <table className={styles.table}>
                            <thead
                                className={styles.head}
                                style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}
                            >
                                <tr>
                                    <th className={styles.th} style={thStyles}>Sno.</th>
                                    <th className={styles.th} style={thStyles}>Request Id</th>
                                    <th className={styles.th} style={thStyles}>Created At</th>
                                    <th className={styles.th} style={thStyles}>Status</th>
                                    <th className={styles.th} style={thStyles}>Upload Count</th>
                                    <th className={styles.th} style={thStyles}>Uploaded File</th>
                                    <th className={styles.th} style={thStyles}>Processed File</th>
                                </tr>
                            </thead>

                            <tbody>
                                {uploadedFiles.map((item: any, index: number) => (
                                    <tr key={index} className={styles.no}>
                                        <td className={styles.td} style={tdStyles}>{index + 1}</td>
                                        <td className={styles.td} style={tdStyles}>{item.request_id}</td>
                                        <td className={styles.td} style={tdStyles}>
                                            {moment(item.created_at.toDate()).format("MM/DD/YY HH:mm")}
                                        </td>
                                        <td className={styles.td} style={tdStyles}>{item.status}</td>
                                        <td className={styles.td} style={tdStyles}>{item.uploadCount}</td>

                                        <td className={styles.td} style={tdStyles}>
                                            <a href={item.fileUrl}>Uploaded File</a>
                                        </td>

                                        <td className={styles.td} style={tdStyles}>
                                            {item.responseURL !== "" && (
                                                <a href={item.responseURL}>Processed File</a>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    {/* ACTION BUTTON SECTION */}
                    <div className={styles.selectBox}>
                        {/* CANCEL BUTTON */}
                        <div style={{ marginRight: 8 }}>
                            <StyledCancelButton onClick={close}>
                                Cancel
                            </StyledCancelButton>
                        </div>

                        {/* CSV TEMPLATE DOWNLOAD */}
                        <div style={{ marginRight: 8 }}>
                            <CSVGenerator data={apiHeaderData} />
                        </div>

                        {/* FILE UPLOAD */}
                        <div>
                            <input
                                id="file-input"
                                className={styles.select}
                                type="file"
                                accept=".csv,.xlsx,.xls"
                                style={{ display: "none" }}
                                onChange={(e: any) => upload(e.target.files[0])}
                            />

                            <StyledSubmitButton
                                onClick={() => document.getElementById("file-input")?.click()}
                            >
                                Upload File
                            </StyledSubmitButton>
                        </div>
                    </div>

                    {/* FOOTER NOTES */}
                    <div style={{ textAlign: "center", marginTop: "1rem" }}>
                        <p style={{ fontFamily: "var(--font-family-main)", fontSize: "var(--font-size-small)" }}>
                            <i className="fas fa-info-circle"></i> Only .csv file type is supported.
                        </p>
                        <p
                            style={{
                                marginTop: "-1rem",
                                fontFamily: "var(--font-family-main)",
                                fontSize: "var(--font-size-small)"
                            }}
                        >
                            <i className="fas fa-info-circle"></i> Please ensure filling all the mandatory fields.
                        </p>
                    </div>
                </div>
            </Modal>
        </>
    );
};

const mapStateToProps = (state: any) => {
    return {
        theme: state.theme.isLightMode,
        apiHeaders: state.apiHeaderList.data,
    };
};

export default connect(mapStateToProps)(NewImportApiModal);
